/**
 * When a failed mutation may be sent again automatically.
 *
 * A retry is only safe when sending the request twice cannot do two things.
 * Two failures used to break that:
 *
 *   - REJECTIONS WERE RESENT. The old predicate read `error.status`, which
 *     fetchFromNextJs never set (it sets `httpStatus`), and otherwise looked for
 *     a 4xx number in the message text. Most messages carry none, so validation
 *     rejections (an invalid IIN, a 409 conflict) went out twice.
 *   - CREATES WERE RESENT. A POST that failed with a server error or a dropped
 *     connection was retried, but those failures say nothing about whether the
 *     write landed: a 502/504 from the proxy, or a connection cut after the
 *     backend committed, turns a retry into a second booking or a second
 *     WhatsApp message to a patient.
 *
 * So: never resend what the server rejected; never resend a POST, a DELETE, or
 * any request whose method is unknown; resend PUT, PATCH or GET once after a
 * server error or a transport failure.
 *
 * DELETE IS NOT RESENT (review #8 L5). It is idempotent in effect but not in
 * answer: resent after the first attempt committed, it finds the row already
 * soft-deleted and gets a 404 (DeleteAppointment's AutoBranchGuard, for one),
 * so the user sees an error toast for a delete that worked. A delete that
 * really failed is one click to repeat.
 */
import { httpMethodOf, httpStatusOf } from "./nextjs-fetch";

const RETRYABLE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "PUT", "PATCH"]);

/** The most retries any mutation gets. */
export const MAX_MUTATION_RETRIES = 1;

function statusOf(error: Error): number | undefined {
  const fromFetch = httpStatusOf(error);
  if (fromFetch !== undefined) return fromFetch;
  const legacy = (error as { status?: unknown }).status;
  if (typeof legacy === "number") return legacy;
  // Errors from code that does not go through fetchFromNextJs (uploads,
  // hand-rolled fetches) sometimes only carry the status in their text.
  const inText = Number(error.message.match(/\b([45]\d\d)\b/)?.[1]);
  return Number.isFinite(inText) ? inText : undefined;
}

export function shouldRetryMutation(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_MUTATION_RETRIES) return false;
  if (!(error instanceof Error)) return false;
  // The session is gone or a redirect is already under way.
  if (error.message.startsWith("AUTH_ERROR")) return false;

  const status = statusOf(error);
  // The server's answer, not an accident: it will answer the same again.
  if (status !== undefined && status >= 400 && status < 500) return false;

  // A 5xx or a transport failure: safe to resend only when the method is
  // idempotent. Unknown means the error did not come from fetchFromNextJs, so
  // there is no way to tell — don't risk a duplicate write.
  const method = httpMethodOf(error);
  return method !== undefined && RETRYABLE_METHODS.has(method);
}
